import { useSyncExternalStore } from 'react';
import type { VersionInfo } from './versionCheck';

/**
 * Who shows the prompt for a new build:
 * - `plain`: the "A new version of Music24 is ready" toast of `AppUpdateNotice`;
 * - `panel`: the What's new update panel (a newer version with published notes, spec 2026-10-20
 *   What's new §7). The panel then owns the prompt, also after "Later".
 */
export type UpdateHandover = 'plain' | 'panel';

/** What `AppUpdateNotice` does for a new build: wait for the What's new host, or one of the two prompts. */
export type UpdatePromptOwner = 'wait' | UpdateHandover;

interface AppUpdateState {
  /** The number of mounted What's new hosts. With none, the plain toast shows at once. */
  hosts: number;
  /** The `version.json` of the new build that the poll of `AppUpdateNotice` found, or null. */
  newBuild: VersionInfo | null;
  /** The decision of the What's new host for each build id. */
  handover: Readonly<Record<string, UpdateHandover>>;
}

const INITIAL: AppUpdateState = { hosts: 0, newBuild: null, handover: {} };

let state: AppUpdateState = INITIAL;
const listeners = new Set<() => void>();

function setState(next: AppUpdateState): void {
  if (next === state) {
    return;
  }
  state = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getState = (): AppUpdateState => state;

/** `AppUpdateNotice` gives the new build that its poll found. One poll for the whole app. */
export function publishNewBuild(info: VersionInfo | null): void {
  if (state.newBuild === info) {
    return;
  }
  setState({ ...state, newBuild: info });
}

/** The What's new host says that it takes part. Returns the call that undoes it (on unmount). */
export function registerUpdateHost(): () => void {
  setState({ ...state, hosts: state.hosts + 1 });
  let registered = true;
  return () => {
    if (!registered) {
      return;
    }
    registered = false;
    setState({ ...state, hosts: Math.max(0, state.hosts - 1) });
  };
}

/**
 * The What's new host decides who shows the prompt for a build. `panel` stays: when the panel
 * owned a build once, the plain toast never comes back for it.
 */
export function handOverUpdate(buildId: string, owner: UpdateHandover): void {
  const current = state.handover[buildId];
  if (current === owner || current === 'panel') {
    return;
  }
  setState({ ...state, handover: { ...state.handover, [buildId]: owner } });
}

/** Who shows the prompt for this build. With no What's new host, the plain toast shows. */
export function promptOwner(current: AppUpdateState, buildId: string): UpdatePromptOwner {
  if (current.hosts === 0) {
    return 'plain';
  }
  return current.handover[buildId] ?? 'wait';
}

/** The new build that the app poll found, or null. */
export function useNewBuild(): VersionInfo | null {
  return useSyncExternalStore(subscribe, () => state.newBuild);
}

/** Who shows the prompt for this build id. Null id: `wait`. */
export function useUpdatePromptOwner(buildId: string | null): UpdatePromptOwner {
  return useSyncExternalStore(subscribe, () => (buildId === null ? 'wait' : promptOwner(getState(), buildId)));
}

/** Tests start from an empty store. */
export function resetAppUpdateStoreForTests(): void {
  setState(INITIAL);
}
